import { useStore } from 'effector-react'
import React, { useState, useCallback } from 'react'
import { AuthCodeChannel } from '~/shared/api'
import { SMS_CODE_LENGTH } from '~/shared/config/constants'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { useSMSCode } from '~/shared/lib/sms'
import { Button } from '~/shared/ui/Buttons'
import { VerificationFieldElement } from '~/shared/ui/form'
import { RepeatCode } from '~/shared/ui/RepeatCode'
import { TIMER_REPEAT_LOGIN_CODE_TIME } from '../constants'
import { goToStepLoginPhone, loginFx } from '../model'
import { LoginStepTitle, GridContainer, GridItem } from '../styled'
import { BackButton, BoxCountDown } from './styled'

type StepLoginCodeProps = {
  onLogin: (code: string) => void
  onCodeRepeat: () => void
  onSendSms: () => void
  repeatCodeStorageKey: string
  channel?: AuthCodeChannel
  maskedEmail: string | null
}

export function StepLoginCode({
  onLogin,
  onCodeRepeat,
  onSendSms,
  repeatCodeStorageKey,
  channel,
  maskedEmail,
}: StepLoginCodeProps) {
  const [code, setCode] = useState<string>()
  const [isTimerActive, setIsTimerActive] = useState(true)
  const isLoginPending = useStore(loginFx.pending)

  const handleCodeChange = useCallback(
    (code: string) => {
      setCode(code)
      if (code.length === SMS_CODE_LENGTH) {
        onLogin(code)
      }
    },
    [onLogin],
  )

  const { error } = useSMSCode({
    onCode: handleCodeChange,
  })

  const handleLogin = () => {
    if (error) {
      snackbarEnqueued({ message: error, variant: 'danger' })
      return
    }

    if (!code) {
      snackbarEnqueued({ message: 'Ошибка не найден код', variant: 'danger' })
      return
    }

    onLogin(code)
    setCode(undefined)
  }

  const isEmailChannel = channel === 'email'

  return (
    <div>
      <LoginStepTitle>
        {isEmailChannel
          ? `Введите код, который пришел на почту ${maskedEmail}`
          : 'Введите код, который пришел на указанный номер'}
      </LoginStepTitle>

      <GridContainer>
        <GridItem>
          <VerificationFieldElement
            key={`${isLoginPending}`}
            onChange={handleCodeChange}
            value={code}
            autoFocus
          />
          <Button
            variant='limeFlooded'
            fullWidth
            loading={isLoginPending}
            onClick={handleLogin}
            disabled={code?.length !== 4}
            style={{
              marginTop: '16px',
            }}
          >
            Войти
          </Button>
          <BoxCountDown>
            <RepeatCode
              key={channel}
              onCodeRepeat={onCodeRepeat}
              time={TIMER_REPEAT_LOGIN_CODE_TIME}
              storageKey={repeatCodeStorageKey}
              onActiveChange={setIsTimerActive}
            />
          </BoxCountDown>
          {isEmailChannel && !isTimerActive && (
            <Button
              variant='textLink'
              fullWidth
              onClick={onSendSms}
              contentProps={{ style: { padding: 0 } }}
              style={{ marginTop: '8px' }}
            >
              Не получили письмо? Отправить код по SMS
            </Button>
          )}
        </GridItem>
        <GridItem style={{ textAlign: 'center' }}>
          <BackButton
            onClick={() => goToStepLoginPhone()}
            variant='textLink'
            fullWidth
          >
            Назад
          </BackButton>
        </GridItem>
      </GridContainer>
    </div>
  )
}
